/**
 * @fileoverview Sounds Component
 * 
 * A generic component that listens for sound triggers and plays
 * the appropriate sounds using the use-sound library. This component accepts
 * sound state via props, making it reusable across different stores.
 * 
 * @module Common/Sounds
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import useSound from 'use-sound';

import newSessionSound from './files/new-session.mp3';
import newMessageSound from './files/new-message.mp3';
import handRaisedSound from './files/hand-raised.wav';
import audioCheck from './files/audio-check.mp3';

/**
 * Sound Configuration
 * 
 * CENTRALIZED SOUND CONFIGURATION - Add all new sounds here!
 * 
 * To add a new sound, follow these steps:
 * 
 * 1. Import the sound file at the top of this file:
 *    import myNewSound from './files/my-new-sound.mp3';
 * 
 * 2. Add an entry to SOUND_CONFIG below:
 *    'my-sound-name': {
 *        file: myNewSound,
 *        options: { volume: 0.5 }
 *    }
 * 
 * 3. Add a useSound hook call in the Sounds component:
 *    const [playMySound] = useSound(SOUND_CONFIG['my-sound-name'].file, SOUND_CONFIG['my-sound-name'].options);
 * 
 * 4. Add the sound to the soundMap:
 *    'my-sound-name': playMySound,
 * 
 * 5. Add the play function to the useEffect dependencies:
 *    }, [soundToPlay, allowSounds, clearSound, playNewSession, playNewMessage, playMySound]);
 * 
 * That's it! You can now trigger the sound with:
 * useAppStore.getState().playSound('my-sound-name');
 */
const SOUND_CONFIG = {
    'new-session': {
        file: newSessionSound,
        options: { volume: 0.5 }
    },
    'new-message': {
        file: newMessageSound,
        options: { volume: 0.5 }
    },
    'hand-raised': {
        file: handRaisedSound,
        options: { volume: 0.5 }
    },
    'audio-check': {
        file: audioCheck,
        options: { volume: 0.5 }
    }
};

/**
 * Sounds Component
 * 
 * A generic sound player that accepts sound state via props.
 * Plays sounds when triggered and respects user sound preferences.
 * Supports custom options for each sound playback.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.soundToPlay - Sound trigger object with { name, options }
 * @param {Function} props.clearSound - Function to clear the sound trigger after playing
 * @param {boolean} props.allowSounds - Whether sounds are enabled
 * 
 * @example
 * // Using with AppStore
 * const soundToPlay = useAppStore((state) => state.soundToPlay);
 * const clearSound = useAppStore((state) => state.clearSound);
 * const allowSounds = useAppStore((state) => state.allowSounds);
 * 
 * <Sounds 
 *   soundToPlay={soundToPlay}
 *   clearSound={clearSound}
 *   allowSounds={allowSounds}
 * />
 * 
 * @example
 * // Using with any custom store
 * const soundToPlay = useMyStore((state) => state.soundToPlay);
 * const clearSound = useMyStore((state) => state.clearSound);
 * const allowSounds = useMyStore((state) => state.allowSounds);
 * 
 * <Sounds 
 *   soundToPlay={soundToPlay}
 *   clearSound={clearSound}
 *   allowSounds={allowSounds}
 * />
 */
function Sounds({ soundToPlay, clearSound, allowSounds = true }) {

    // Load all sounds from config using useSound
    // Each sound is loaded as a separate hook call (hooks must be called unconditionally)
    // useSound returns [play, { stop, pause, duration, sound }]
    const [playNewSession, { stop: stopNewSession, sound: soundNewSession }] = useSound(SOUND_CONFIG['new-session'].file, SOUND_CONFIG['new-session'].options);
    const [playNewMessage, { stop: stopNewMessage, sound: soundNewMessage }] = useSound(SOUND_CONFIG['new-message'].file, SOUND_CONFIG['new-message'].options);
    const [playHandRaised, { stop: stopHandRaised, sound: soundHandRaised }] = useSound(SOUND_CONFIG['hand-raised'].file, SOUND_CONFIG['hand-raised'].options);
    const [playAudioCheck, { stop: stopAudioCheck, sound: soundAudioCheck }] = useSound(SOUND_CONFIG['audio-check'].file, SOUND_CONFIG['audio-check'].options);

    // Map sound names to their play, stop functions, and sound objects
    const soundMap = {
        'new-session': { play: playNewSession, stop: stopNewSession, sound: soundNewSession },
        'new-message': { play: playNewMessage, stop: stopNewMessage, sound: soundNewMessage },
        'hand-raised': { play: playHandRaised, stop: stopHandRaised, sound: soundHandRaised },
        'audio-check': { play: playAudioCheck, stop: stopAudioCheck, sound: soundAudioCheck }
    };

    // Effect to play or stop sounds when triggered
    useEffect(() => {
        if (soundToPlay) {
            const { name, options = {} } = soundToPlay;
            const soundObj = soundMap[name];

            if (soundObj) {
                // Check if this is a stop action
                if (options.action === 'stop') {
                    soundObj.stop();
                } else if (options.action === 'setVolume' && soundObj.sound) {
                    // Adjust volume of currently playing sound
                    soundObj.sound.volume(options.volume);
                } else if (allowSounds) {
                    // Play the sound with custom options
                    // The options object can include: volume, playbackRate, interrupt, onend, etc.
                    soundObj.play(options);
                }
            } else {
                console.warn(`Unknown sound: ${name}`);
            }

            // Clear the sound trigger after playing/stopping/adjusting
            clearSound();
        }
    }, [soundToPlay, allowSounds, clearSound, playNewSession, playNewMessage, playHandRaised, playAudioCheck]);

    // This component doesn't render anything
    return null;
}

Sounds.propTypes = {
    soundToPlay: PropTypes.shape({
        name: PropTypes.string.isRequired,
        options: PropTypes.object
    }),
    clearSound: PropTypes.func.isRequired,
    allowSounds: PropTypes.bool
};

export default Sounds;
